import React from 'react';
import { useSelector } from 'react-redux';

import { USER_MODE } from 'modules/mediaLibrary/constants';
import { getCurrentUserId } from 'modules/users/selectors/currentUser';

const PREFERENCES_LOCAL_STORAGE_PREFIX = 'USER_PREFERENCES_';
const getUserLocalStoragePreferencesKey = userId => `${PREFERENCES_LOCAL_STORAGE_PREFIX}${userId}`;

export const USER_PREFERENCES = {
  MEDIA_LIBRARY: 'MEDIA_LIBRARY',
  COOKIES: 'COOKIES',
  MOBILITY_SIGN_ZONE: 'MOBILITY_SIGN_ZONE',
  MOBILITY_DEFAULT_DOCUMENT: 'MOBILITY_DEFAULT_DOCUMENT',
  SELF_SERVICE_DEFAULT_DOCUMENT: 'SELF_SERVICE_DEFAULT_DOCUMENT',
  WORK_ORDER_SIGN_ZONE: 'WORK_ORDER_SIGN_ZONE',
  LAST_RELEASE_NOTE: 'LAST_RELEASE_NOTE',
  KANBAN_COLLAPSED_COLUMNS: 'KANBAN_COLLAPSED_COLUMNS',
};

const defaultPreferences = {
  [USER_PREFERENCES.MEDIA_LIBRARY]: USER_MODE,
  [USER_PREFERENCES.KANBAN_COLLAPSED_COLUMNS]: {},
};

const getPreferences = key => ({
  ...defaultPreferences,
  ...(JSON.parse(localStorage.getItem(key)))
});

export function getLocalUserPreferences(userId) {
  const preferencesKey = getUserLocalStoragePreferencesKey(userId);
  return getPreferences(preferencesKey);
}

const useLocalUserPreferences = () => {
  const userId = useSelector(getCurrentUserId);

  const preferencesKey = getUserLocalStoragePreferencesKey(userId);
  const [preferences, setPreferences] = React.useState(getPreferences(preferencesKey));

  const setPreference = React.useCallback((key, value) => {
    const newPreferences = {
      ...preferences,
      [key]: value,
    };

    localStorage.setItem(preferencesKey, JSON.stringify(newPreferences));
    setPreferences(newPreferences);
  }, [preferences, preferencesKey]);

  React.useEffect(() => {
    const handler = () => setPreferences(getPreferences(preferencesKey));

    // We get the preferences from the local storage whenever the preferences key changes
    handler();
    // And we listen to changes in the local storage to update the preferences
    window.addEventListener('localstorage', handler);

    return () => window.removeEventListener('localstorage', handler);
  }, [preferencesKey]);

  return React.useMemo(() => ({
    ...preferences,
    setPreference
  }), [preferences, setPreference]);
};

export default useLocalUserPreferences;
